import {
  useGetAlphaBscPositionsQuery,
  useGetDipDcaStateQuery,
  useGetGridStatusQuery,
  useGetHourlyMetricsQuery,
  useGetInventorySwingStatusQuery,
  useGetRegimeCurrentQuery,
  useGetSettingsQuery,
  useGetStatusQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlphaLossCapPill, useAlphaLossCap, utcEpoch } from "@/components/domain/AlphaLossCapPill";

/**
 * EngineHealthStrip — one compact cell per engine with a colored status
 * dot + a one-line caption. The "is everything alive?" glance row.
 *
 *   green = enabled + recent activity
 *   amber = enabled but idle / suppressed (e.g. risk_off, 0 fills)
 *   red   = paused by a breaker (auto-pause, loss-cap)
 *   gray  = disabled
 *
 * Sources ONLY endpoints the Overview page (or its cards) already poll —
 * every hook below matches an existing subscription's args so RTK Query
 * dedupes the network traffic. No new endpoints, no new poll cadences.
 */

type DotTone = "good" | "warn" | "bad" | "off";

const DOT: Record<DotTone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  off: "bg-muted",
};

interface EngineCellData {
  key: string;
  label: string;
  tone: DotTone;
  caption: string;
}

interface GridFill { ts?: string | null }
interface GridSymbolInfo { recent_fills?: GridFill[] }
interface InvSwingState { updated_at?: string | null }
interface InvSwingStatus {
  per_asset_states?: InvSwingState[];
  aggregate?: { n?: number; total_pnl?: number; wins?: number };
}
interface DipDcaAssetState { last_fill_at?: string | null }
interface AlphaPosition { exit_ts?: string | null }

const DAY_MS = 24 * 3600 * 1000;

function rel(epoch: number): string {
  const diff = Date.now() - epoch;
  if (!Number.isFinite(diff)) return "?";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Latest timestamp as epoch ms; 0 when none parse. */
function maxEpoch(timestamps: Array<string | null | undefined>): number {
  return timestamps.reduce<number>((m, ts) => {
    const t = utcEpoch(ts);
    return Number.isFinite(t) && t > m ? t : m;
  }, 0);
}

export function EngineHealthStrip() {
  // All args mirror subscriptions Overview / its cards already hold,
  // so these resolve from the RTK Query cache.
  const settingsQ = useGetSettingsQuery();
  const status = useGetStatusQuery();                                            // 10s via Overview
  const grid = useGetGridStatusQuery(undefined, { pollingInterval: 30000 });     // SystemPictureCard
  const invSwing = useGetInventorySwingStatusQuery(undefined, { pollingInterval: 30000 }); // InventorySwingCard
  const dipDca = useGetDipDcaStateQuery(undefined, { pollingInterval: 60000 });  // DipDcaCard
  const regime = useGetRegimeCurrentQuery(undefined, { pollingInterval: 60_000 }); // MarketRegimePill
  const hourlyMetrics = useGetHourlyMetricsQuery(50, { pollingInterval: 60000 }); // Overview
  const alphaClosed = useGetAlphaBscPositionsQuery("closed", { pollingInterval: 120000 }); // shared w/ pill
  const alphaCap = useAlphaLossCap();

  const settings = (settingsQ.data ?? {}) as Record<string, unknown>;
  const loading = settingsQ.isLoading && status.isLoading;

  // --- Grid: enabled flag + most recent live fill across symbols
  const gridEnabled = !!settings.grid_trader_enabled;
  const gridLive = !!grid.data?.live_enabled;
  const gridBySymbol = (grid.data?.by_symbol ?? {}) as Record<string, GridSymbolInfo>;
  const gridLastFill = maxEpoch(
    Object.values(gridBySymbol).map((s) => s.recent_fills?.[0]?.ts),
  );
  const gridCell: EngineCellData = {
    key: "grid",
    label: "Grid",
    tone: !gridEnabled ? "off"
      : gridLastFill && Date.now() - gridLastFill < DAY_MS ? "good" : "warn",
    caption: !gridEnabled ? "disabled"
      : gridLastFill ? `${gridLive ? "live" : "paper"} · fills ${rel(gridLastFill)}`
      : `${gridLive ? "live" : "paper"} · no fills yet`,
  };

  // --- Inventory swing: state rows carry updated_at; aggregate carries cycle count
  const invEnabled = !!settings.inventory_swing_enabled;
  const invData = invSwing.data as InvSwingStatus | undefined;
  const invLast = maxEpoch((invData?.per_asset_states ?? []).map((s) => s.updated_at));
  const invCycles = Number(invData?.aggregate?.n ?? 0);
  const invCell: EngineCellData = {
    key: "inv_swing",
    label: "Inv Swing",
    tone: !invEnabled ? "off"
      : invLast && Date.now() - invLast < DAY_MS ? "good" : "warn",
    caption: !invEnabled ? "disabled"
      : invLast ? `${invCycles} cycles · ${rel(invLast)}` : "armed · no cycles yet",
  };

  // --- Dip-DCA: ticking constantly, only buys at fresh 7d-lows
  const dipEnabled = !!settings.dip_dca_enabled;
  const dipStates = (dipDca.data?.state ?? []) as DipDcaAssetState[];
  const dipLastFill = maxEpoch(dipStates.map((s) => s.last_fill_at));
  const dipCell: EngineCellData = {
    key: "dip_dca",
    label: "Dip DCA",
    tone: !dipEnabled ? "off"
      : dipLastFill && Date.now() - dipLastFill < DAY_MS ? "good" : "warn",
    caption: !dipEnabled ? "disabled"
      : dipLastFill ? `last buy ${rel(dipLastFill)}` : "ticking · waiting for 7d-low",
  };

  // --- Smart DCA: suppressed whenever the master regime overlay says risk_off
  const smartEnabled = !!settings.smart_dca_enabled;
  const regimeState = regime.data?.state;
  const smartCell: EngineCellData = {
    key: "smart_dca",
    label: "Smart DCA",
    tone: !smartEnabled ? "off" : regimeState === "risk_off" ? "warn" : "good",
    caption: !smartEnabled ? "disabled"
      : regimeState === "risk_off" ? "suppressed (risk_off)"
      : `armed · ${regimeState ?? "regime ?"}`,
  };

  // --- Hourly cycle: top-level paused flag + auto-pause breaker
  const hm = hourlyMetrics.data;
  const hourlyPaused = !!settings.paused;
  const tradesToday = Number(status.data?.trades_today ?? 0);
  const hourlyCell: EngineCellData = {
    key: "hourly",
    label: "Hourly",
    tone: hourlyPaused && hm?.auto_pause_tripped ? "bad"
      : hourlyPaused ? "warn"
      : tradesToday > 0 ? "good" : "warn",
    caption: hourlyPaused && hm?.auto_pause_tripped ? "auto-paused (breaker)"
      : hourlyPaused ? "paused"
      : tradesToday > 0 ? `${tradesToday} trades today` : "ticking · mostly HOLD",
  };

  // --- Alpha BSC: loss-cap breaker > enabled/idle
  const alphaEnabled = !!settings.alpha_bsc_auto_enabled;
  const alphaLastExit = maxEpoch(
    ((alphaClosed.data ?? []) as AlphaPosition[]).map((p) => p.exit_ts),
  );
  const alphaCell: EngineCellData = {
    key: "alpha_bsc",
    label: "Alpha BSC",
    tone: alphaEnabled && alphaCap.capHit ? "bad"
      : !alphaEnabled ? "off"
      : alphaLastExit && Date.now() - alphaLastExit < DAY_MS ? "good" : "warn",
    caption: alphaEnabled && alphaCap.capHit ? "paused — 24h loss cap"
      : !alphaEnabled ? "disabled"
      : alphaLastExit ? `flat · last exit ${rel(alphaLastExit)}` : "flat · no exits yet",
  };

  const cells = [gridCell, invCell, dipCell, smartCell, hourlyCell, alphaCell];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between flex-wrap gap-2 pb-2">
        <CardTitle>
          Engine health
          <span className="text-xs font-normal text-muted ml-2">
            green = live & active · amber = idle/suppressed · red = breaker · gray = off
          </span>
        </CardTitle>
        <AlphaLossCapPill />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-14 w-full" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2">
            {cells.map((c) => (
              <div key={c.key} className="border border-border bg-bg rounded p-2 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${DOT[c.tone]}`} />
                  <span className="text-xs font-medium truncate">{c.label}</span>
                </div>
                <div className="text-[11px] text-muted truncate mt-0.5" title={c.caption}>
                  {c.caption}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
